import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Megaphone } from "lucide-react";
import { api, errText } from "../../lib/api";
import { Badge, Drawer, Panel, Stat, TabBar, money, stamp } from "./ui";

const LOCALES = ["en", "fr", "es", "ar"];
const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (n) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);
const dayOf = (iso) => (iso ? String(iso).slice(0, 10) : "");
const toIso = (day, endOfDay) => (day ? `${day}T${endOfDay ? "23:59:59" : "00:00:00"}+00:00` : "");

const EMPTY_OFFER = {
  name: "", kind: "sitewide", type: "percentage", value: 10, city_ids: [], occasion_ids: [],
  buyer_countries: [], min_order_mad: 0, starts_at: today(), ends_at: plusDays(14),
  is_active: true, announce: false, headline: {},
};
const EMPTY_COUPON = {
  code: "", label: "", type: "percentage", value: 10, city_ids: [], occasion_ids: [], buyer_countries: [],
  email: "", min_order_mad: 0, first_order_only: false, usage_limit: 0, per_customer_limit: 1,
  starts_at: today(), expires_at: plusDays(30), is_active: true, announce: false, headline: {},
};

const statusTone = (s) => (s === "live" ? "good" : s === "scheduled" ? "brass" : "plain");

// The code attached to the basket-recovery e-mail that already goes out 30 minutes after someone leaves.
const BasketCouponPanel = () => {
  const [form, setForm] = useState(null);
  const [msg, setMsg] = useState("");
  useEffect(() => {
    api.get("/admin/settings").then((r) => setForm({
      basket_coupon_enabled: r.data.basket_coupon_enabled || "on",
      basket_coupon_percent: r.data.basket_coupon_percent || "10",
      basket_coupon_hours: r.data.basket_coupon_hours || "24",
      basket_coupon_min_order_mad: r.data.basket_coupon_min_order_mad || "0",
    })).catch(() => {});
  }, []);
  const save = async () => {
    setMsg("");
    try { await api.put("/admin/settings", form); setMsg("Saved."); } catch (e) { setMsg(errText(e)); }
  };
  if (!form) return null;
  return (
    <Panel title="Abandoned-basket coupon" testid="basket-coupon-panel">
      <p className="text-xs text-muted">
        Thirty minutes after a buyer walks away from checkout we e-mail their saved basket back to them. With this on, the
        e-mail carries a single-use code tied to their address, already applied when they open the link — so a near-miss
        still converts. It expires on its own, and it comes out of the Fleurinelle margin.
      </p>
      {msg ? <p className="text-xs text-brass" data-testid="basket-coupon-message">{msg}</p> : null}
      <div className="grid md:grid-cols-4 gap-3">
        <label className="block text-xs">
          <span className="text-ink font-medium">Coupon in the e-mail</span>
          <select className="field mt-1" data-testid="basket-coupon-enabled" value={form.basket_coupon_enabled}
                  onChange={(e) => setForm({ ...form, basket_coupon_enabled: e.target.value })}>
            <option value="on">On</option>
            <option value="off">Off — plain reminder only</option>
          </select>
        </label>
        <label className="block text-xs">
          <span className="text-ink font-medium">Percent off</span>
          <input className="field mt-1 tabular" data-testid="basket-coupon-percent" value={form.basket_coupon_percent}
                 onChange={(e) => setForm({ ...form, basket_coupon_percent: e.target.value.replace(/[^0-9]/g, "") })} />
        </label>
        <label className="block text-xs">
          <span className="text-ink font-medium">Valid for (hours)</span>
          <input className="field mt-1 tabular" data-testid="basket-coupon-hours" value={form.basket_coupon_hours}
                 onChange={(e) => setForm({ ...form, basket_coupon_hours: e.target.value.replace(/[^0-9]/g, "") })} />
        </label>
        <label className="block text-xs">
          <span className="text-ink font-medium">Minimum basket (MAD)</span>
          <input className="field mt-1 tabular" data-testid="basket-coupon-min" value={form.basket_coupon_min_order_mad}
                 onChange={(e) => setForm({ ...form, basket_coupon_min_order_mad: e.target.value.replace(/[^0-9]/g, "") })} />
        </label>
      </div>
      <button className="btn-primary text-xs px-4 w-fit" data-testid="basket-coupon-save" onClick={save}>Save</button>
    </Panel>
  );
};

const Field = ({ label, hint, children }) => (
  <label className="block text-xs">
    <span className="text-ink font-medium">{label}</span>
    {children}
    {hint ? <span className="text-muted block mt-1">{hint}</span> : null}
  </label>
);

const MultiPicker = ({ options, selected, onChange, testid, empty = "Everywhere" }) => (
  <div className="flex flex-wrap gap-1.5 mt-1" data-testid={testid}>
    {options.length === 0 ? <span className="text-xs text-muted">{empty}</span> : null}
    {options.map((o) => {
      const on = selected.includes(o.id);
      return (
        <button key={o.id} type="button" data-testid={`${testid}-${o.id}`}
                className={`text-[11px] border px-2 py-1 transition-colors ${on ? "bg-ink text-plaster border-ink" : "border-brass-light text-muted hover:border-brass"}`}
                onClick={() => onChange(on ? selected.filter((x) => x !== o.id) : [...selected, o.id])}>
          {o.name}
        </button>
      );
    })}
  </div>
);

export default function DiscountsPage() {
  const [tab, setTab] = useState("auto");
  const [data, setData] = useState(null);
  const [perf, setPerf] = useState(null);
  const [msg, setMsg] = useState("");
  const [draft, setDraft] = useState(null); // {mode:'offer'|'coupon', doc}

  const load = useCallback(async () => {
    try {
      const [o, p] = await Promise.all([api.get("/admin/offers"), api.get("/admin/offers/performance").catch(() => ({ data: { rows: [] } }))]);
      setData(o.data); setPerf(p.data);
    } catch (e) { setMsg(errText(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => {
    const rows = perf?.rows || [];
    return {
      orders: rows.reduce((a, r) => a + r.orders, 0),
      discount: rows.reduce((a, r) => a + r.discount_mad, 0),
      margin: rows.reduce((a, r) => a + r.margin_mad, 0),
      live: (data?.promotions || []).filter((p) => p.status === "live").length
        + (data?.coupons || []).filter((c) => c.status === "live").length,
    };
  }, [perf, data]);

  const save = async () => {
    const { mode, doc } = draft;
    const body = mode === "offer"
      ? { ...doc, starts_at: toIso(doc.starts_at), ends_at: toIso(doc.ends_at, true) }
      : { ...doc, starts_at: toIso(doc.starts_at), expires_at: toIso(doc.expires_at, true) };
    setMsg("");
    try {
      if (mode === "offer") {
        if (doc.id) await api.put(`/admin/offers/${doc.id}`, body); else await api.post("/admin/offers", body);
      } else if (doc.id) await api.put(`/admin/coupons/${doc.id}`, body);
      else await api.post("/admin/coupons", body);
      setDraft(null); setMsg("Saved."); load();
    } catch (e) { setMsg(errText(e)); }
  };

  const remove = async (mode, id) => {
    setMsg("");
    try { await api.delete(`/admin/${mode === "offer" ? "offers" : "coupons"}/${id}`); load(); }
    catch (e) { setMsg(errText(e)); }
  };

  const openOffer = (row) => setDraft({ mode: "offer", doc: row
    ? { ...EMPTY_OFFER, ...row, starts_at: dayOf(row.starts_at), ends_at: dayOf(row.ends_at), headline: row.headline || {} }
    : { ...EMPTY_OFFER } });
  const openCoupon = (row) => setDraft({ mode: "coupon", doc: row
    ? { ...EMPTY_COUPON, ...row, starts_at: dayOf(row.starts_at), expires_at: dayOf(row.expires_at), headline: row.headline || {} }
    : { ...EMPTY_COUPON } });

  if (!data) return <p className="text-sm text-muted" data-testid="discounts-loading">Loading…</p>;
  const stat = (key) => perf?.rows?.find((r) => r.key === key);

  return (
    <div className="space-y-5" data-testid="admin-discounts-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl text-ink">Discounts & coupons</h1>
          <p className="text-sm text-muted max-w-2xl">
            Automatic offers apply with no code — sitewide, for chosen cities or for chosen occasions. Coupon codes are for
            campaigns you advertise. Only the best single offer ever applies to a basket, and every dirham comes out of the
            Fleurinelle margin: partner payouts stay at the cost price you set on the bouquet.
          </p>
        </div>
      </div>
      {msg ? <p className="text-sm text-brass" data-testid="discounts-message">{msg}</p> : null}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Live offers" value={totals.live} testid="discounts-stat-live" />
        <Stat label="Discounted orders" value={totals.orders} testid="discounts-stat-orders" />
        <Stat label="Discount given (MAD)" value={money(totals.discount)} testid="discounts-stat-given" />
        <Stat label="Margin kept (MAD)" value={money(totals.margin)} tone={totals.margin <= 0 ? "bad" : undefined} testid="discounts-stat-margin" />
      </div>

      <TabBar testid="discounts-tabs" value={tab} onChange={setTab}
              tabs={[["auto", "Automatic offers", data.promotions.length], ["codes", "Coupon codes", data.coupons.length]]} />

      {tab === "auto" ? (
        <Panel title="Automatic offers" testid="offers-panel"
               action={<button className="btn-primary text-xs px-3 inline-flex items-center gap-1" onClick={() => openOffer(null)} data-testid="offer-new">
                 <Plus size={13} /> New offer</button>}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="smallcaps border-b border-brass-light text-start">
                <th className="p-2 text-start">Name</th><th className="p-2 text-start">Applies to</th><th className="p-2">Off</th>
                <th className="p-2 text-start">Window</th><th className="p-2">Used</th><th className="p-2">Status</th><th className="p-2" />
              </tr></thead>
              <tbody>
                {data.promotions.map((p) => (
                  <tr key={p.id} className="border-b border-brass-light/60" data-testid={`offer-row-${p.id}`}>
                    <td className="p-2">
                      <button className="underline decoration-brass underline-offset-2" onClick={() => openOffer(p)} data-testid={`offer-edit-${p.id}`}>{p.name || "Untitled"}</button>
                      {p.announce ? <Megaphone size={12} className="inline ms-1 text-brass" /> : null}
                    </td>
                    <td className="p-2 text-muted">{p.kind === "sitewide" ? "Whole site" : p.kind === "city" ? `${(p.city_ids || []).length} cities` : `${(p.occasion_ids || []).length} occasions`}
                      {p.min_order_mad ? ` · min ${money(p.min_order_mad)} MAD` : ""}{(p.buyer_countries || []).length ? ` · ${p.buyer_countries.join(", ")}` : ""}</td>
                    <td className="p-2 text-center tabular">{p.type === "percentage" ? `${p.value}%` : `${money(p.value)} MAD`}</td>
                    <td className="p-2 text-muted whitespace-nowrap">{dayOf(p.starts_at)} → {dayOf(p.ends_at)}</td>
                    <td className="p-2 text-center tabular">{stat(p.id)?.orders ?? p.used_count ?? 0}</td>
                    <td className="p-2 text-center"><Badge tone={statusTone(p.status)}>{p.status}</Badge></td>
                    <td className="p-2 text-end"><button className="text-muted hover:text-danger" onClick={() => remove("offer", p.id)} data-testid={`offer-delete-${p.id}`}><Trash2 size={13} /></button></td>
                  </tr>
                ))}
                {data.promotions.length === 0 ? <tr><td colSpan={7} className="p-4 text-muted text-sm" data-testid="offers-empty">No automatic offer yet.</td></tr> : null}
              </tbody>
            </table>
          </div>
        </Panel>
      ) : null}

      {tab === "codes" ? (
        <Panel title="Coupon codes" testid="coupons-panel"
               action={<button className="btn-primary text-xs px-3 inline-flex items-center gap-1" onClick={() => openCoupon(null)} data-testid="coupon-new">
                 <Plus size={13} /> New code</button>}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="smallcaps border-b border-brass-light text-start">
                <th className="p-2 text-start">Code</th><th className="p-2 text-start">Rules</th><th className="p-2">Off</th>
                <th className="p-2 text-start">Window</th><th className="p-2">Used</th><th className="p-2">Status</th><th className="p-2" />
              </tr></thead>
              <tbody>
                {data.coupons.map((c) => (
                  <tr key={c.id} className="border-b border-brass-light/60" data-testid={`coupon-row-${c.id}`}>
                    <td className="p-2">
                      <button className="underline decoration-brass underline-offset-2 font-mono text-xs" onClick={() => openCoupon(c)} data-testid={`coupon-edit-${c.id}`}>{c.code}</button>
                      {c.announce ? <Megaphone size={12} className="inline ms-1 text-brass" /> : null}
                    </td>
                    <td className="p-2 text-muted">
                      {[c.min_order_mad ? `min ${money(c.min_order_mad)} MAD` : "", c.first_order_only ? "first order" : "",
                        c.email ? c.email : "", (c.buyer_countries || []).length ? c.buyer_countries.join(", ") : "",
                        (c.city_ids || []).length ? `${c.city_ids.length} cities` : "",
                        (c.occasion_ids || []).length ? `${c.occasion_ids.length} occasions` : "",
                        c.usage_limit ? `cap ${c.usage_limit}` : "", c.per_customer_limit ? `${c.per_customer_limit}/buyer` : ""]
                        .filter(Boolean).join(" · ") || "No restriction"}
                    </td>
                    <td className="p-2 text-center tabular">{c.type === "percentage" ? `${c.value}%` : `${money(c.value)} MAD`}</td>
                    <td className="p-2 text-muted whitespace-nowrap">{dayOf(c.starts_at)} → {dayOf(c.expires_at)}</td>
                    <td className="p-2 text-center tabular">{c.used_count || 0}</td>
                    <td className="p-2 text-center"><Badge tone={statusTone(c.status)}>{c.status}</Badge></td>
                    <td className="p-2 text-end"><button className="text-muted hover:text-danger" onClick={() => remove("coupon", c.id)} data-testid={`coupon-delete-${c.id}`}><Trash2 size={13} /></button></td>
                  </tr>
                ))}
                {data.coupons.length === 0 ? <tr><td colSpan={7} className="p-4 text-muted text-sm" data-testid="coupons-empty">No code yet.</td></tr> : null}
              </tbody>
            </table>
          </div>
        </Panel>
      ) : null}

      <BasketCouponPanel />

      {perf?.rows?.length ? (        <Panel title="What each offer cost and kept" testid="offer-performance">
          <table className="w-full text-sm">
            <thead><tr className="smallcaps border-b border-brass-light"><th className="p-2 text-start">Offer</th><th className="p-2">Orders</th><th className="p-2">Revenue</th><th className="p-2">Discount</th><th className="p-2">Margin</th></tr></thead>
            <tbody>
              {perf.rows.map((r) => (
                <tr key={r.key} className="border-b border-brass-light/60" data-testid={`perf-row-${r.key}`}>
                  <td className="p-2">{r.name}</td>
                  <td className="p-2 text-center tabular">{r.orders}</td>
                  <td className="p-2 text-center tabular">{money(r.revenue_mad)}</td>
                  <td className="p-2 text-center tabular text-warning">−{money(r.discount_mad)}</td>
                  <td className={`p-2 text-center tabular ${r.margin_mad <= 0 ? "text-danger" : "text-success"}`}>{money(r.margin_mad)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      ) : null}

      {draft ? (
        <Drawer testid="offer-drawer" onClose={() => setDraft(null)}
                title={draft.mode === "offer" ? (draft.doc.id ? "Edit offer" : "New automatic offer") : (draft.doc.id ? "Edit code" : "New coupon code")}
                subtitle={draft.mode === "offer" ? "Applies with no code at checkout" : "Buyers type this at checkout"}
                actions={<button className="btn-primary text-xs px-4" onClick={save} data-testid="offer-save">Save</button>}>
          <div className="grid md:grid-cols-2 gap-4">
            {draft.mode === "offer" ? (
              <Field label="Name (internal)"><input className="field mt-1" data-testid="offer-name" value={draft.doc.name}
                     onChange={(e) => setDraft({ ...draft, doc: { ...draft.doc, name: e.target.value } })} /></Field>
            ) : (
              <Field label="Code" hint="Buyers type this exactly — letters and numbers.">
                <input className="field mt-1 font-mono uppercase" data-testid="coupon-code" value={draft.doc.code}
                       onChange={(e) => setDraft({ ...draft, doc: { ...draft.doc, code: e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, "") } })} />
              </Field>
            )}
            <Field label="Discount">
              <div className="flex gap-2 mt-1">
                <select className="field" data-testid="offer-type" value={draft.doc.type}
                        onChange={(e) => setDraft({ ...draft, doc: { ...draft.doc, type: e.target.value } })}>
                  <option value="percentage">% off the bouquet</option>
                  <option value="fixed">MAD off the bouquet</option>
                </select>
                <input className="field w-24 tabular" type="number" min="0" data-testid="offer-value" value={draft.doc.value}
                       onChange={(e) => setDraft({ ...draft, doc: { ...draft.doc, value: Number(e.target.value) } })} />
              </div>
            </Field>
            {draft.mode === "offer" ? (
              <Field label="Applies to">
                <select className="field mt-1" data-testid="offer-kind" value={draft.doc.kind}
                        onChange={(e) => setDraft({ ...draft, doc: { ...draft.doc, kind: e.target.value } })}>
                  <option value="sitewide">The whole site</option>
                  <option value="city">Chosen cities</option>
                  <option value="occasion">Chosen occasions</option>
                </select>
              </Field>
            ) : (
              <Field label="Reserved for (e-mail)" hint="Leave empty for everyone.">
                <input className="field mt-1" data-testid="coupon-email" value={draft.doc.email}
                       onChange={(e) => setDraft({ ...draft, doc: { ...draft.doc, email: e.target.value } })} />
              </Field>
            )}
            <Field label="Minimum basket (MAD)">
              <input className="field mt-1 tabular" type="number" min="0" data-testid="offer-min"
                     value={draft.doc.min_order_mad} onChange={(e) => setDraft({ ...draft, doc: { ...draft.doc, min_order_mad: Number(e.target.value) } })} />
            </Field>
            <Field label="Starts">
              <input className="field mt-1" type="date" data-testid="offer-starts" value={draft.doc.starts_at}
                     onChange={(e) => setDraft({ ...draft, doc: { ...draft.doc, starts_at: e.target.value } })} />
            </Field>
            <Field label="Ends">
              <input className="field mt-1" type="date" data-testid="offer-ends"
                     value={draft.mode === "offer" ? draft.doc.ends_at : draft.doc.expires_at}
                     onChange={(e) => setDraft({ ...draft, doc: { ...draft.doc, [draft.mode === "offer" ? "ends_at" : "expires_at"]: e.target.value } })} />
            </Field>
            {draft.mode === "coupon" ? (
              <>
                <Field label="Total uses (0 = unlimited)">
                  <input className="field mt-1 tabular" type="number" min="0" data-testid="coupon-usage-limit" value={draft.doc.usage_limit}
                         onChange={(e) => setDraft({ ...draft, doc: { ...draft.doc, usage_limit: Number(e.target.value) } })} />
                </Field>
                <Field label="Uses per buyer (0 = unlimited)">
                  <input className="field mt-1 tabular" type="number" min="0" data-testid="coupon-per-customer" value={draft.doc.per_customer_limit}
                         onChange={(e) => setDraft({ ...draft, doc: { ...draft.doc, per_customer_limit: Number(e.target.value) } })} />
                </Field>
                <label className="flex items-center gap-2 text-xs text-ink">
                  <input type="checkbox" checked={!!draft.doc.first_order_only} data-testid="coupon-first-order"
                         onChange={(e) => setDraft({ ...draft, doc: { ...draft.doc, first_order_only: e.target.checked } })} />
                  First order only
                </label>
              </>
            ) : null}
            <label className="flex items-center gap-2 text-xs text-ink">
              <input type="checkbox" checked={!!draft.doc.is_active} data-testid="offer-active"
                     onChange={(e) => setDraft({ ...draft, doc: { ...draft.doc, is_active: e.target.checked } })} />
              Active
            </label>
            <label className="flex items-center gap-2 text-xs text-ink">
              <input type="checkbox" checked={!!draft.doc.announce} data-testid="offer-announce"
                     onChange={(e) => setDraft({ ...draft, doc: { ...draft.doc, announce: e.target.checked } })} />
              Advertise in the storefront banner
            </label>
          </div>

          <div className="space-y-2">
            <p className="text-xs text-ink font-medium">Cities {draft.mode === "offer" && draft.doc.kind === "city" ? "(required)" : "(optional — leave empty for all)"}</p>
            <MultiPicker options={data.cities} selected={draft.doc.city_ids || []} testid="offer-cities"
                         onChange={(v) => setDraft({ ...draft, doc: { ...draft.doc, city_ids: v } })} />
            <p className="text-xs text-ink font-medium pt-2">Occasions {draft.mode === "offer" && draft.doc.kind === "occasion" ? "(required)" : "(optional)"}</p>
            <MultiPicker options={data.occasions} selected={draft.doc.occasion_ids || []} testid="offer-occasions"
                         onChange={(v) => setDraft({ ...draft, doc: { ...draft.doc, occasion_ids: v } })} />
            <Field label="Buyer countries (optional)" hint="Two-letter codes, comma separated — e.g. FR, ES, US. Empty = everywhere.">
              <input className="field mt-1" data-testid="offer-countries" value={(draft.doc.buyer_countries || []).join(", ")}
                     onChange={(e) => setDraft({ ...draft, doc: { ...draft.doc, buyer_countries: e.target.value.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean) } })} />
            </Field>
          </div>

          {draft.doc.announce ? (
            <div className="space-y-2">
              <p className="text-xs text-ink font-medium">Banner wording</p>
              {LOCALES.map((l) => (
                <input key={l} className="field text-xs" data-testid={`offer-headline-${l}`} placeholder={`${l.toUpperCase()} — e.g. 10% off every bouquet until Sunday`}
                       value={(draft.doc.headline || {})[l] || ""}
                       onChange={(e) => setDraft({ ...draft, doc: { ...draft.doc, headline: { ...draft.doc.headline, [l]: e.target.value } } })} />
              ))}
            </div>
          ) : null}
          {draft.doc.id ? <p className="text-[11px] text-muted">Created {stamp(draft.doc.created_at)}</p> : null}
        </Drawer>
      ) : null}
    </div>
  );
}
